// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import React, { FC, useEffect, useId, useState } from 'react';
import styled from 'styled-components';
import Tooltip from '@mui/material/Tooltip';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import { useSelector } from 'react-redux';

import { Dialog, IconButton2, buttonVariants } from '@clatter/ui';
import { captureError, hasRole, useDeepCompareMemo, userRolesMap } from '@clatter/platform';
import {
  useGetUsersQuery,
  useShareFolderMutation,
  useCreateGroupMutation,
  useRevokeFolderMutation,
  useGetFolderGrantsQuery,
  useShareWorkspaceMutation,
  useGetWorkspaceGrantsQuery,
  useRevokeWorkspaceGrantMutation,
  useGetGroupsByMeQuery,
  useGetSlideRepositoryGrantsQuery,
  useShareSlideRepositoryMutation,
  useRevokeSlideRepositoryGrantMutation,
} from '../../../services';
import { authSelectors } from '../../../store';
import { CircularLoader } from '../../../shared';
import { DropdownSelect, NgButton, NgDivider } from '../../UI';
import { GrantAccessLevel, GrantAccessLevelToDisplayNameMap } from '../../../constants';

import { AccessList } from '../../AccessList/AccessList';
import { FolderManagerDialogAddForm } from './FolderManagerDialogAddForm';

//region STYLED
const StyledContainer = styled.div`
  width: 50vw;
  max-width: 600px;
  min-height: 40vh;
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: center;
  align-items: center;

  .form-control {
    min-height: 80px;
  }

  .header {
    border-bottom: 1px solid lightgrey;
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.medium};
    margin-bottom: auto;
    flex: 1;
    width: 100%;
    overflow-y: auto;
  }

  .footer {
    width: 100%;
  }

  .buttons {
    width: 100%;
    display: flex;
    justify-content: space-between;
    gap: 15px;

    .right-section,
    .left-section {
      flex: 1;
      display: flex;
      gap: 20px;
    }

    .right-section {
      justify-content: flex-end;
    }
  }

  .react-select__menu {
    background: red;
  }
`;

const StyledItemsToShareList = styled.ul`
  height: 100%;
  width: 100%;
  padding: 0;
  margin: 0;
  list-style: none;

  font-size: ${({ theme }) => theme.fontSize.default};
`;

const StyledSelectContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.medium};

  & > :first-child {
    flex-grow: 1;
  }

  & > :nth-child(2) {
    min-width: 120px;
    flex-shrink: 0;
  }
`;

const StyledOptionContainer = styled.div`
  display: flex;
  flex-direction: column;

  .label {
    font-size: ${({ theme }) => theme.fontSize.default};
  }

  .details {
    font-size: ${({ theme }) => theme.fontSize.default};
  }
`;
//endregion

const accessTypeOptions: IDropdownSelectOptionItem[] = [
  { value: GrantAccessLevel.VIEWER, label: GrantAccessLevelToDisplayNameMap[GrantAccessLevel.VIEWER] },
  { value: GrantAccessLevel.EDITOR, label: GrantAccessLevelToDisplayNameMap[GrantAccessLevel.EDITOR] },
  { value: GrantAccessLevel.DATA_ADMIN, label: GrantAccessLevelToDisplayNameMap[GrantAccessLevel.DATA_ADMIN] },
];

const optionTypesMap = {
  GROUP: 'groups',
  USER: 'users',
};

const prefixMap = {
  FOLDERS: 'folders',
  WORKSPACES: 'workspaces',
  SLIDE_REPOSITORIES: 'slide-repositories',
};

const filterOption = (candidate: any, input: any) => {
  const lowerInput = input.toLowerCase();

  const labelMatch = candidate.label.toLowerCase().includes(lowerInput);
  const emailMatch = candidate.data?.data?.email && candidate.data.data.email.toLowerCase().includes(lowerInput);

  return labelMatch || emailMatch;
};

const formatOptionLabel = ({ label, data, type }: any, { context }: any) => {
  if (context === 'menu') {
    return (
      <StyledOptionContainer style={{ display: 'flex', flexDirection: 'column' }}>
        <span className="label">{label}</span>

        <div className="details">
          {type === optionTypesMap.USER && <span style={{ fontSize: '12px' }}>{data.email}</span>}
          {type === optionTypesMap.GROUP && <span style={{ fontSize: '12px' }}>[Group]</span>}
        </div>
      </StyledOptionContainer>
    );
  }

  return (
    <StyledOptionContainer>
      <span className="label">{label}</span>{' '}
    </StyledOptionContainer>
  );
};

const ItemsToShareList = ({ selectedRows }: { selectedRows: any[] }) => {
  const id = useId();
  return (
    <StyledItemsToShareList>
      {selectedRows.map((item) => (
        <li key={`${id}_${item?.id}`}>{item?.displayName || item?.name || item?.title}</li>
      ))}
    </StyledItemsToShareList>
  );
};

interface FolderManagerShareDialogProps {
  selectedRows: FolderManagerItem[];
  customTitle?: string;
  isLoading?: boolean;
  opened: boolean;
  onDismiss: (data: null) => void;
  prefix?: 'folders' | 'workspaces' | 'presentations' | 'slide-repositories';
}

export const FolderManagerShareDialog: FC<FolderManagerShareDialogProps> = ({
  opened,
  onDismiss,
  customTitle,
  selectedRows,
  prefix = prefixMap.FOLDERS,
  isLoading: isParentLoading,
}) => {
  //@todo - remove
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<IDropdownSelectOptionItem[] | null>(null);
  const [selectedAccessType, setSelectedAccessType] = useState<IDropdownSelectOptionItem>(accessTypeOptions[0]);

  const [isUpdating, setIsUpdating] = useState(false);

  const [showAddForm, setShowAddForm] = useState(false);

  const title = customTitle || `Share ${selectedRows?.length} item(s).`;

  const activeUser = useSelector(authSelectors.getUserAuthState)?.user;

  const { data: usersList, isLoading: isUsersLoading, isError: userFetchingError } = useGetUsersQuery();
  const { data: groupsList, isLoading: isGroupsListLoading } = useGetGroupsByMeQuery();

  const [addGroup, { isLoading: isAddGroupLoading }] = useCreateGroupMutation();

  //folders
  // NOTE for now we do not support multi share of folders, hence we can use selectedRows?.[0]?.id
  const [shareFolder, { isLoading: isShareFolderLoading }] = useShareFolderMutation();
  const [revokeFolder, { isLoading: isRevokeFolderLoading }] = useRevokeFolderMutation();

  const { data: folderGrants, isLoading: isGetFolderGrantsLoading } = useGetFolderGrantsQuery(
    {
      folderId: selectedRows?.[0]?.id,
    },
    {
      skip: !selectedRows?.[0]?.id || prefix !== prefixMap.FOLDERS,
    },
  );

  //workspaces
  // NOTE for now we do not support multi share of workspaces, hence we can use selectedRows?.[0]?.id
  const { data: workspaceGrants, isLoading: isWorkspaceGrantsLoading } = useGetWorkspaceGrantsQuery(
    { workspaceId: selectedRows?.[0]?.id },
    {
      skip: !selectedRows?.[0]?.id || prefix !== prefixMap.WORKSPACES,
    },
  );

  const [shareWorkspace, { isLoading: isShareWorkspaceLoading }] = useShareWorkspaceMutation();
  const [revokeWorkspace, { isLoading: isRevokeWorksapceLoading }] = useRevokeWorkspaceGrantMutation();

  // slide-repositories
  const { data: slideRepositoryGrants, isLoading: isSlideRepositoryGrantsLoading } = useGetSlideRepositoryGrantsQuery(
    { repositoryId: selectedRows?.[0]?.id },
    {
      skip: !selectedRows?.[0]?.id || prefix !== prefixMap.SLIDE_REPOSITORIES,
    },
  );

  const [shareSlideRepository, { isLoading: isShareSlideRepositoryLoading }] = useShareSlideRepositoryMutation();
  const [revokeSlideRepository, { isLoading: isRevokeSlideRepositoryLoading }] = useRevokeSlideRepositoryGrantMutation();

  const isLoading =
    isUpdating ||
    isUsersLoading ||
    isParentLoading ||
    isAddGroupLoading ||
    isGroupsListLoading ||
    isShareFolderLoading ||
    isRevokeFolderLoading ||
    isGetFolderGrantsLoading ||
    isRevokeWorksapceLoading ||
    isWorkspaceGrantsLoading ||
    isShareWorkspaceLoading ||
    isSlideRepositoryGrantsLoading ||
    isShareSlideRepositoryLoading ||
    isRevokeSlideRepositoryLoading;

  const isSubmitDisabled = isLoading || (selectedOptions || [])?.length === 0;

  const mapGrantsToDisplay = (grants: IGrant[] | undefined) => {
    if (!grants || grants.length <= 0) return [];

    return grants.map((item: any) => {
      if (item?.type === optionTypesMap.USER && !userFetchingError) {
        const user = (usersList || []).find((user: any) => user.id === item.id);
        return {
          ID: item?.id,
          Name: `${user?.first_name} ${user?.last_name}`,
          Role: item?.role,
          Type: item?.type,
        };
      }

      const group = (groupsList || []).find((group: any) => group.id === item.id);
      return {
        ID: item?.id,
        Name: group?.name,
        Role: item?.role,
        Type: item?.type,
      };
    });
  };

  const workspaceMappedGrants = useDeepCompareMemo(
    () => mapGrantsToDisplay(workspaceGrants),
    [workspaceGrants, usersList, groupsList, userFetchingError],
  );

  const slideRepositoryMappedGrants = useDeepCompareMemo(
    () => mapGrantsToDisplay(slideRepositoryGrants),
    [slideRepositoryGrants, usersList, groupsList, userFetchingError],
  );

  const dropdownOptions = useDeepCompareMemo(() => {
    const groups = (groupsList || []).map((item: any) => ({
      value: item.id,
      label: item.name,
      type: optionTypesMap.GROUP,
      data: item,
    }));

    const users = (usersList || []).map((item) => ({
      value: item.id,
      label: `${item?.first_name} ${item?.last_name}`,
      type: optionTypesMap.USER,
      data: item,
    }));

    return [...groups, ...users];
  }, [groupsList, usersList]);

  const handleDialogDismiss = () => {
    onDismiss(null);
  };

  const handleAddFormAction = async (actionType: string, data: any) => {
    if (actionType === 'submit' && data) {
      await addGroup({ data: { Name: data?.name } });
    }
    setShowAddForm(false);
  };

  const handleShare = async () => {
    if (!groupsList) {
      console.log('Error: groups list not provided!');
      return;
    }

    if (!selectedOptions) {
      captureError(new Error('Error: Could not share item(s): no selected options!'), { component: 'FolderManagerShareDialog', skipConsole: true });
      return;
    }

    //@todo - this is not working as expected
    if (errorMessages?.length > 0) {
      setErrorMessages([]);
    }

    const sharePromises: Promise<any>[] = [];

    setIsUpdating(true);

    try {
      for (const item of selectedRows) {
        const { id: itemToShareId } = item || {};

        if (!itemToShareId) {
          captureError(new Error('Error: no folder id provided!'), { component: 'FolderManagerShareDialog', skipConsole: true });
          continue;
        }

        // create promise for each selected option
        for (const option of selectedOptions) {
          const payload: SharePayload = {
            role: selectedAccessType.value,
          };

          if (option?.type === optionTypesMap.GROUP) {
            payload.group_id = +option.value;
          }

          if (option?.type === optionTypesMap.USER) {
            payload.user_id = +option.value;
          }

          if (prefix === prefixMap.WORKSPACES) {
            sharePromises.push(shareWorkspace({ workspaceId: itemToShareId, data: payload }));
          } else if (prefix === prefixMap.SLIDE_REPOSITORIES) {
            sharePromises.push(shareSlideRepository({ repositoryId: itemToShareId, data: payload }));
          } else {
            sharePromises.push(shareFolder({ folderId: itemToShareId, data: payload }));
          }
        }

        const responses = await Promise.all(sharePromises);

        const errors: string[] = [];

        for (const response of responses) {
          if (response['error']?.data) {
            errors.push(`Error sharing workspace: "${item?.name}", reason: ${response['error'].data}.`);
          }
        }

        //@todo - for now, to avoid the dialog being flooded with error messages.
        if (errors?.length) {
          setErrorMessages([errors[0]]);
          return;
        }

        // reset state
        setSelectedAccessType(accessTypeOptions[0]);
        setSelectedOptions(null);
      }

      setIsUpdating(false);
      return;
    } catch (error: any) {
      captureError(error, { component: 'FolderManagerShareDialog' });
      setIsUpdating(false);
      setErrorMessages(['Error sharing item(s)']);
      return;
    }
  };

  const handleRevokeGrant = async (action: NgAction<any>) => {
    const { payload: grant } = action || {};

    if (!selectedRows?.[0]?.id) {
      return;
    }

    if (!grant) {
      setErrorMessages(['Error revoking grant: no payload!']);
      return;
    }

    setIsUpdating(true);

    const grantPayload = {
      [grant?.Type === optionTypesMap.USER ? 'user_id' : 'group_id']: grant.ID,
    } as SharePayload;

    if (prefix === prefixMap.WORKSPACES) {
      await revokeWorkspace({ workspaceId: selectedRows[0].id, data: grantPayload });
      setIsUpdating(false);
      return;
    }

    if (prefix === prefixMap.SLIDE_REPOSITORIES) {
      await revokeSlideRepository({ repositoryId: selectedRows[0].id, data: grantPayload });
      setIsUpdating(false);
      return;
    }

    //@todo handle new grant types
    await revokeFolder({
      folderId: selectedRows[0].id,
      groupId: +grant.ID,
    });

    setIsUpdating(false);
  };

  const appliedGrants =
    prefix === prefixMap.FOLDERS
      ? folderGrants
      : prefix === prefixMap.SLIDE_REPOSITORIES
        ? slideRepositoryMappedGrants
        : workspaceMappedGrants;

  const isAdmin = useDeepCompareMemo(() => hasRole(userRolesMap.ngAdmin, activeUser), [activeUser]);

  useEffect(() => {
    if (userFetchingError) {
      setErrorMessages(['Failed to fetch user data.']);
    }
  }, [userFetchingError]);

  if (!opened) {
    return null;
  }

  return (
    <>
      <Dialog title={title} onCloseDialog={() => handleDialogDismiss()} open={opened} zIndex={99999} noBorder includeFooter>
      <StyledContainer>
        {isGroupsListLoading ? (
          <CircularLoader />
        ) : (
          <>
            {showAddForm ? (
              <FolderManagerDialogAddForm
                submitLabel="Add Group"
                isLoading={isLoading}
                onAction={handleAddFormAction}
              />
            ) : (
              <>
                <div className="content">
                  <div style={{ flex: 0.1 }}>
                    <ItemsToShareList selectedRows={selectedRows} />
                  </div>

                  <NgDivider spacing="small" />

                  <StyledSelectContainer>
                    <DropdownSelect
                      placeholder="Add Groups or Users"
                      value={selectedOptions}
                      options={dropdownOptions}
                      filterOption={filterOption}
                      formatOptionLabel={formatOptionLabel}
                      onChange={setSelectedOptions}
                      isMulti
                    />

                    <DropdownSelect
                      options={accessTypeOptions}
                      value={selectedAccessType}
                      onChange={setSelectedAccessType}
                    />
                  </StyledSelectContainer>

                  <div>
                    <h4>Users and Groups with access:</h4>

                    <AccessList list={appliedGrants} isLoading={isLoading} onAction={handleRevokeGrant} />
                  </div>
                </div>

                <div className="footer">
                  {errorMessages.map((message, index) => (
                    <p style={{ color: 'red' }} key={index}>
                      {message}
                    </p>
                  ))}

                  <div className="buttons">
                    <div className="left-section">
                      {isAdmin && (
                        <Tooltip title="Add Group" placement="top">
                          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                          {/*@ts-ignore*/}
                          <IconButton2 onClick={() => setShowAddForm(true)}>
                            <GroupAddIcon />
                          </IconButton2>
                        </Tooltip>
                      )}
                    </div>

                    <div className="right-section">
                      <NgButton
                        testId="folder-manager-move-dialog-cancel-button"
                        onClick={() => handleDialogDismiss()}
                        variant={buttonVariants.clear}
                        type="button"
                      >
                        Cancel
                      </NgButton>

                      <NgButton
                        testId="folder-manager-move-dialog-submit-button"
                        onClick={() => handleShare()}
                        variant={buttonVariants.primary}
                        disabled={isSubmitDisabled}
                      >
                        Share
                      </NgButton>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </StyledContainer>
      </Dialog>
    </>
  );
};
